import type { PaymentMethodType } from '@/lib/api/billing';

/**
 * Every named event in the app, grouped into one catalogue per domain.
 * Title Case per the approved convention (EE-498); property keys are
 * lowercase snake_case to match the `app`, `environment` and `page` keys
 * @upms/analytics stamps on every event.
 *
 * No provider appears in a name — `provider` is a property, so a future
 * Zuora-to-Stripe migration never forks the catalogue and cross-provider
 * conversion stays one funnel.
 *
 * This object is the artefact reviewed against the convention document; keep
 * the catalogue table in the EE-674 plan in step with it.
 */
export const POLICY_EVENTS = {
  coiGenerationStarted: 'COI Generation Started',
  // Exactly one of Completed / Failed fires per generate attempt — never both,
  // never a `result` property on either. The event name is the outcome, so a
  // Lexicon reader never finds "Completed" carrying a failure.
  coiGenerationCompleted: 'COI Generation Completed',
  coiGenerationFailed: 'COI Generation Failed',
  paymentMethodUpdateStarted: 'Payment Method Update Started',
  // Exactly one of Completed / Failed / Abandoned fires per dialog close —
  // same reasoning as the COI pair above, extended to the third outcome.
  paymentMethodUpdateCompleted: 'Payment Method Update Completed',
  paymentMethodUpdateFailed: 'Payment Method Update Failed',
  paymentMethodUpdateAbandoned: 'Payment Method Update Abandoned',
} as const;

export type PolicyEventName = (typeof POLICY_EVENTS)[keyof typeof POLICY_EVENTS];

export const CONSENT_EVENTS = {
  consentPromptDisplayed: 'Consent Prompt Displayed',
  consentPromptCompleted: 'Consent Prompt Completed',
  consentPromptFailed: 'Consent Prompt Failed',
  consentPromptDismissed: 'Consent Prompt Dismissed',
} as const;

export type ConsentEventName = (typeof CONSENT_EVENTS)[keyof typeof CONSENT_EVENTS];

export type PaymentProvider = 'stripe' | 'zuora' | 'unknown';

/**
 * Aliased, not restated, so a billing-contract change cannot drift from the
 * event. Note there is a second, unrelated `PaymentMethodType` in
 * `lib/api/billing/types.ts` that carries `last4` — never use that one here.
 */
export type PaymentMethodCategory = PaymentMethodType;
